<script>
import Vue from "vue";
import db from "@/db"; // Adjust this path to your db.js file location
import { mapState } from "vuex";

// A trail set can reference several hundred images. Downloading them all at
// once exhausts the connection pool on mobile and keeps every blob in memory,
// so the fetches run through a fixed pool of workers instead.
const IMAGE_CONCURRENCY = 6;
const IMAGE_TIMEOUT_MS = 30000;
const IMAGE_ATTEMPTS = 2;

export default {
  name: "Data", // Replace with your actual component name
  data() {
    return {
      progress: 0,
      progressMessage: "",
      downloadedDataSize: 0,
      isSyncing: false,
    };
  },
  computed: {
    ...mapState(["apiUrl", "customerId"]),
    downloadedDataSizeMB() {
      return (this.downloadedDataSize / (1024 * 1024)).toFixed(2);
    },
  },
  mounted() {
    //localStorage.setItem("dbVersion", 0);
    window.addEventListener("online", this.handleOnlineStatus);

    this.$store.dispatch("loadData");
    this.updateDatabaseVersionCheck();
  },
  beforeUnmount() {
    window.removeEventListener("online", this.handleOnlineStatus);
  },
  methods: {
    showStatus(message, progress = 0, hideAfter = 0) {
      this.$store.commit("setProgress", progress);
      this.$store.commit("setProgressMessage", message);
      this.$store.commit("setLoading", true);
      if (hideAfter) {
        setTimeout(() => this.$store.commit("setLoading", false), hideAfter);
      }
    },
    handleOnlineStatus() {
      if (navigator.onLine) {
        // The browser has regained access to the network
        this.updateDatabaseVersionCheck();
      }
    },
    // Method to download an image and return its blob. A stalled request on a
    // weak mobile connection would otherwise hang the whole sync, so each
    // attempt is capped by a timeout and transient failures are retried once.
    async downloadImageAsBlob(imageUrl) {
      for (let attempt = 1; attempt <= IMAGE_ATTEMPTS; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), IMAGE_TIMEOUT_MS);
        try {
          const response = await fetch(imageUrl, { signal: controller.signal });
          if (!response.ok) {
            // A missing or forbidden image will never appear on a retry.
            if (response.status >= 400 && response.status < 500) {
              console.error(
                `Image unavailable (${response.status}):`,
                imageUrl
              );
              return null;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const blob = await response.blob();

          // Update the downloaded data size
          this.downloadedDataSize += blob.size;
          return blob;
        } catch (error) {
          if (attempt === IMAGE_ATTEMPTS) {
            console.error("Failed to download image:", imageUrl, error);
            return null;
          }
        } finally {
          clearTimeout(timer);
        }
      }
      return null;
    },
    // Method to insert or update an image blob in the database and return its ID
    async insertOrUpdateImageBlob(imageUrl, imageBlob) {
      try {
        let imageRecord = await db.images.where({ imageUrl }).first();
        if (imageRecord) {
          await db.images.update(imageRecord.id, { blob: imageBlob });
          return imageRecord.id;
        } else {
          const imageId = await db.images.add({ imageUrl, blob: imageBlob });
          return imageId;
        }
      } catch (error) {
        console.error("Error inserting or updating image blob:", error);
        return null;
      }
    },
    // Main method to process and insert data
    // In your data.vue component
    async insertData(jsonData) {
      // Build one unique URL list before starting any requests. The API can
      // reference the same image hundreds of times; each URL must be fetched
      // only once and its IndexedDB ID reused everywhere.
      const imageUrls = new Set();
      const addImageUrl = url => {
        if (typeof url === "string" && url.trim()) imageUrls.add(url.trim());
      };
      jsonData.forEach(card => {
        addImageUrl(card.CoverImage);
        addImageUrl(card.ElevationProfile);
        (card.POI || []).forEach(poi => addImageUrl(poi.Image));
        (card.Gallery || []).forEach(item => addImageUrl(item.Url));
      });

      const uniqueImageUrls = [...imageUrls];
      const totalImages = uniqueImageUrls.length;
      let completedDownloads = 0;
      let failedImages = 0;

      // Function to update progress
      const updateProgress = () => {
        const progressPercentage = totalImages
          ? (completedDownloads / totalImages) * 100
          : 0;
        this.$store.dispatch("updateProgress", {
          progress: progressPercentage,
          message: `Downloading images: ${completedDownloads} of ${totalImages}`,
        });
      };
      updateProgress();

      // Each worker takes the next URL and writes its blob to IndexedDB as
      // soon as it arrives, so only IMAGE_CONCURRENCY blobs are ever held in
      // memory at once.
      const imageIds = new Map();
      let nextIndex = 0;
      const downloadWorker = async () => {
        while (nextIndex < totalImages) {
          const imageUrl = uniqueImageUrls[nextIndex++];
          const blob = await this.downloadImageAsBlob(imageUrl);
          if (blob) {
            const imageId = await this.insertOrUpdateImageBlob(imageUrl, blob);
            if (imageId === null) {
              failedImages++;
            } else {
              imageIds.set(imageUrl, imageId);
            }
          } else {
            failedImages++;
          }
          completedDownloads++;
          updateProgress();
        }
      };

      await Promise.all(
        Array.from(
          { length: Math.min(IMAGE_CONCURRENCY, totalImages) },
          downloadWorker
        )
      );

      // The URL set is trimmed, so lookups have to be trimmed to match.
      const imageIdFor = url =>
        (typeof url === "string" ? imageIds.get(url.trim()) : null) ?? null;

      // Inserted one at a time: a failure has to be attributable to a single
      // trail, and concurrent write transactions gain nothing here.
      const failedCards = [];
      for (const sourceCard of jsonData) {
        try {
          await this.insertCard(sourceCard, imageIdFor);
        } catch (error) {
          console.error("Failed to insert trail:", sourceCard?.Name, error);
          failedCards.push(sourceCard?.Name ?? sourceCard?.CardId ?? "unknown");
        }
      }

      // Throwing here is what stops the caller from recording this version as
      // successfully downloaded - a partial database must be retried, not
      // remembered as up to date.
      if (failedCards.length) {
        throw new Error(
          `Failed to insert ${failedCards.length} of ${jsonData.length} trails: ${failedCards.join(", ")}`
        );
      }

      if (failedImages) {
        console.warn(`${failedImages} of ${totalImages} images unavailable.`);
      }

      // Final progress update
      this.$store.dispatch("updateProgress", {
        progress: 100,
        message: failedImages
          ? `Data loading complete (${failedImages} images unavailable).`
          : "Data loading complete!",
      });
      this.$store.commit("setDataLoaded", true);
      return { totalImages, failedImages };
    },
    // Insert a single trail plus its POIs. The API returns null for POI and
    // Gallery on some cards, so both are normalised before use.
    async insertCard(sourceCard, imageIdFor) {
      const card = JSON.parse(JSON.stringify(sourceCard));
      const gallery = Array.isArray(card.Gallery) ? card.Gallery : [];
      const pois = Array.isArray(card.POI) ? card.POI : [];
      const coverImageId = imageIdFor(card.CoverImage);
      const elevationImageId = imageIdFor(card.ElevationProfile);

      await db.transaction("rw", db.cards, db.pois, async () => {
        gallery.forEach(item => {
          item.imageId = imageIdFor(item.Url);
          delete item.Url;
        });

        pois.forEach(poi => {
          poi.imageId = imageIdFor(poi.Image);
        });

        const cardDataWithBlobReferences = {
          ...card,
          CoverImage: coverImageId,
          ElevationProfile: elevationImageId,
          Gallery: gallery,
          POI: pois,
        };

        const cardId = await db.cards.add(cardDataWithBlobReferences);

        for (const poi of pois) {
          const exists = await db.pois.get(poi.MarkerId);
          if (!exists) {
            await db.pois.put({
              ...poi,
              rootId: cardId,
            });
          }
        }
      });
    },
    getStoredVersion() {
      const version = parseInt(localStorage.getItem("dbVersion"), 10);
      return Number.isFinite(version) ? version : 0;
    },
    updateStoredVersion(version) {
      localStorage.setItem("dbVersion", version.toString());
    },
    async countCardEntries() {
      return db.cards.count(); // Assuming 'cards' is your table in IndexedDB
    },
    async updateDatabaseVersionCheck() {
      // mounted() and the "online" listener can both fire this, and mobile
      // browsers raise "online" on every network flap. Two concurrent runs
      // would clear the tables underneath each other and insert every trail
      // twice, so only one sync is ever allowed to be in flight.
      if (this.isSyncing) {
        console.log("Trail sync already running - ignoring duplicate trigger.");
        return;
      }
      this.isSyncing = true;
      try {
        await this.runVersionCheck();
      } finally {
        this.isSyncing = false;
      }
    },
    async runVersionCheck() {
      const url = this.apiUrl + "VisitorCard/GetVersion";
      const base64Credentials = btoa("Ian:Ennistymon1!");
      try {
        this.showStatus("Checking for trail updates...");

        // Read the local card count first, and report a failure here
        // separately: if IndexedDB is unusable (private browsing, no free
        // storage, a database left in a bad state) that is not an offline
        // problem, and reporting both the same way hid which one it was.
        let cardCount;
        try {
          cardCount = await this.countCardEntries();
        } catch (dbError) {
          console.error("IndexedDB unavailable:", dbError);
          this.showStatus(
            `Storage unavailable (${dbError.name || "error"}) — trails cannot be saved on this device.`,
            100,
            8000
          );
          return;
        }

        console.log("Checking trail version at", url, "tenant", this.customerId);
        const response = await fetch(url, {
          method: "GET",
          headers: {
            CustomerId: this.customerId,
            Authorization: `Basic ${base64Credentials}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const dbVersion = await response.json();
        const newVersion = Number(dbVersion);
        const storedVersion = this.getStoredVersion();

        // Any difference from the stored version means the local copy is stale,
        // not just a higher number - otherwise a version that gets reset or
        // rolled back in the CMS would leave devices on old data forever.
        if (
          (Number.isFinite(newVersion) && newVersion !== storedVersion) ||
          cardCount === 0
        ) {
          if (cardCount === 0) {
            this.showStatus("No local trails — download required.");
          } else {
            this.showStatus(
              `New trail version ${newVersion} available — download required.`
            );
          }
          console.log("Updating local trail database automatically...");
          const updated = await this.updateDatabase();
          if (updated) {
            // Only recorded once every trail actually made it into IndexedDB,
            // so a failed download is retried on the next launch instead of
            // being remembered as complete.
            if (Number.isFinite(newVersion)) {
              this.updateStoredVersion(newVersion);
            }
            location.reload();
          } else {
            this.showStatus(
              "Trail download failed — it will retry automatically.",
              100,
              5000
            );
          }
        } else {
          console.log("Using stored data, no update needed.");
          this.showStatus(
            `Trails are up to date (version ${newVersion}).`,
            100,
            2500
          );
        }
      } catch (error) {
        console.error("Error fetching version or updating database:", error);
        // A fetch that never reached the server rejects with a TypeError, and
        // says nothing useful in its message on iOS. Naming the two cases
        // apart is what tells us whether the device cannot reach the API or
        // the API answered with an error.
        const detail =
          error.name === "TypeError"
            ? "cannot reach server"
            : error.message || error.name;
        this.showStatus(
          `Offline or update failed (${detail}) — using saved trails.`,
          100,
          8000
        );
      }
    },
    async updateDatabase() {
      const url = this.apiUrl + "VisitorCard/GetCards";
      const base64Credentials = btoa("Ian:Ennistymon1!");
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            CustomerId: this.customerId,
            Authorization: `Basic ${base64Credentials}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();
        await this.deleteAndRecreateDB(jsonData);
        return true;
      } catch (error) {
        console.error("Failed to load and insert data:", error);
        return false;
      }
    },
    async deleteAndRecreateDB(jsonData) {
      console.log("Clearing database tables...");
      // Deliberately not caught here: updateDatabase has to know the insert
      // failed. Leaving the tables cleared is the safe outcome - the empty
      // card count forces a fresh download on the next launch.
      await db.transaction("rw", db.cards, db.pois, db.images, async () => {
        await db.cards.clear();
        await db.pois.clear();
        await db.images.clear();
      });

      console.log("Database tables cleared, inserting new data...");
      await this.insertData(jsonData);
    },
  },
};
</script>
